/**
 * Icon palette for card-laid-out option groups (RadioGroup / CheckboxGroup
 * with `displayAs: 'card'`).
 *
 * Stored workflows persist the registry KEY (`'person'`), never the asset
 * path. That indirection is deliberate: BMO can re-art the icon set, change
 * file formats, or move the assets without a data migration — only this file
 * changes. It also means the builder's icon picker has a finite, curated list
 * to offer instead of a free-text path field nobody can validate.
 *
 * Adding an icon:
 *   1. Drop the asset in `apps/front-end/public/` (same place as the BMO
 *      wordmark the footer uses).
 *   2. Add one entry below.
 *
 * Unknown keys degrade to no icon rather than throwing — a workflow authored
 * against an icon that later gets retired stays renderable.
 */
export type OptionIcon = {
  /** Stable key persisted on `SelectOption.icon`. Never rename — renaming
   *  orphans every workflow that referenced it. Retire instead. */
  name: string;
  /** Public-folder path, served by Vite at the site root. */
  src: string;
  /** Picker labels — what the ORIGINATOR sees when choosing, not advisor-
   *  facing copy, so these live here rather than in the i18n catalog. */
  labelEn: string;
  labelFr: string;
};

export const OPTION_ICONS: readonly OptionIcon[] = [
  {
    name: 'person',
    src: '/person.png',
    labelEn: 'Person',
    labelFr: 'Personne',
  },
  {
    name: 'team',
    src: '/team.png',
    labelEn: 'People',
    labelFr: 'Personnes',
  },
];

const BY_NAME = new Map(OPTION_ICONS.map((i) => [i.name, i]));

/** Resolve a stored key to its registry entry. Returns undefined for unknown
 *  or unset keys so callers can render the card without an icon. */
export function resolveOptionIcon(name: string | null | undefined): OptionIcon | undefined {
  if (!name) return undefined;
  return BY_NAME.get(name);
}
